/* ===================================================================
   Stripe.js loader singleton for the INLINE card-at-send flow
   (card-at-send inline, Steve 2026-07-08, Hopper).

   The embedded Stripe Payment Element needs a PUBLISHABLE key (pk_...) that
   matches the SAME Stripe account/mode the engine's secret key uses. It is
   provided at build time via VITE_STRIPE_PUBLISHABLE_KEY and is safe to ship
   client-side (publishable keys are designed to be public). We NEVER put a
   secret key here.

   GRACEFUL DEGRADATION: when the publishable key is absent (not yet set in the
   build env), `hasStripePublishableKey` is false and `getStripe()` returns
   null. Callers fall back to the existing hosted /choose-plan checkout redirect,
   so the send flow never breaks -- the inline card step simply stays dormant
   until the key is configured, then it activates on the next build.

   loadStripe injects https://js.stripe.com/v3, so the app CSP (public/_headers)
   must allow js.stripe.com in script-src + frame-src and api.stripe.com in
   connect-src.
   =================================================================== */
// '/pure' entry: does NOT auto-inject js.stripe.com on import (unlike the
// default entry). Stripe.js loads only when getStripe() actually calls
// loadStripe (the inline card step), keeping it off every other page.
// Runtime loader from the '/pure' entry (no auto-inject); the `Stripe` TYPE is
// only exported from the main entry, so import it type-only from there (no
// runtime import, so '/pure' stays the sole thing that could load js.stripe.com).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';

const PUBLISHABLE_KEY = (
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined
)?.trim();

/** True only when a publishable key is configured at build time. */
export const hasStripePublishableKey = Boolean(PUBLISHABLE_KEY);

/* Single loadStripe promise, created lazily on first use so the js.stripe.com
   script is only fetched when the inline card step is actually opened. */
let _promise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> | null {
  if (!PUBLISHABLE_KEY) return null;
  if (!_promise) _promise = loadStripe(PUBLISHABLE_KEY);
  return _promise;
}
